import type { ReactNode } from 'react'
import { countGraphemes } from '@/lib/graphemes'
import { X_MAX, xHasLink, xLength } from '@/lib/xcount'
import { cn } from '@/lib/utils'
import { PlatformMark, platformName } from './PlatformMark'

/** Threads refuses a post over 500 characters; Bluesky over 300 graphemes. */
export const THREADS_MAX_CHARS = 500
export const BLUESKY_MAX = 300

/** Channels only ever posted by hand. A desk post on "threads" or "x" is one
 *  too, which only the caller knows (`manual`): a campaign's Threads or X post
 *  goes through the API. */
const MANUAL = new Set(['ppomppu', 'okky', 'instagram', 'tiktok'])

export interface PostPreviewProps {
  platform: string
  /** Reddit's headline; Bluesky's image alt text; a manual post's subject. */
  title?: string
  body: string
  /** The subreddit, or a manual channel's note. */
  channel?: string
  /** The account it goes up as (Threads, Bluesky). */
  account?: string
  flair?: string
  /** The lead image, by media id. */
  mediaId?: number
  /** A desk post on a channel posted by hand ("threads" there is the manual
   *  channel, not a campaign's Threads account). */
  manual?: boolean
  /** Manual channels: the text with the public codes put in. */
  renderedBody?: string
  renderedReply?: string
  codeMode?: 'none' | 'body' | 'reply'
  /** Clamp the body to a few lines (lists, review cards). */
  compact?: boolean
  className?: string
}

/** How a post will read where it goes: Reddit's title, flair and hero over
 *  the body; Threads' bubble with its 500-character count; Bluesky's text with
 *  the 300-grapheme count; a manual channel's text with the codes in place. */
export function PostPreview(props: PostPreviewProps) {
  const { platform, manual } = props
  if (manual || MANUAL.has(platform)) return <ManualPreview {...props} />
  if (platform === 'reddit' || platform === 'reddit_browser') return <RedditPreview {...props} />
  if (platform === 'bluesky') return <CountedPreview {...props} limit={BLUESKY_MAX} unit="graphemes" />
  if (platform === 'threads') return <CountedPreview {...props} limit={THREADS_MAX_CHARS} unit="chars" />
  if (platform === 'x') return <CountedPreview {...props} limit={X_MAX} unit="x" />
  return <CountedPreview {...props} />
}

function Hero({ mediaId, alt, className }: { mediaId?: number; alt?: string; className?: string }) {
  if (!mediaId) return null
  return (
    <img
      src={`/api/media/${mediaId}/file`}
      alt={alt ?? ''}
      loading="lazy"
      className={cn('w-full rounded-md border border-border object-cover', className)}
    />
  )
}

function Body({ text, compact }: { text: string; compact?: boolean }) {
  return (
    <p className={cn('whitespace-pre-wrap break-words text-body text-foreground', compact && 'line-clamp-4')}>
      {text || <span className="text-muted-foreground">본문이 비어 있다</span>}
    </p>
  )
}

function Frame({ children, className }: { children: ReactNode; className?: string }) {
  return <article className={cn('space-y-3 rounded-lg border border-border bg-card p-4', className)}>{children}</article>
}

function RedditPreview({ title, body, channel, account, flair, mediaId, compact, className }: PostPreviewProps) {
  return (
    <Frame className={className}>
      <header className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">
        <PlatformMark platform="reddit" />
        <span className="font-semibold text-foreground">r/{channel || '…'}</span>
        {account && <span>u/{account}</span>}
      </header>
      <div className="space-y-1.5">
        {flair && (
          <span className="inline-flex h-5 items-center rounded-full bg-muted px-2 text-micro font-medium text-foreground">
            {flair}
          </span>
        )}
        <h3 className="text-section font-semibold">{title || <span className="text-muted-foreground">제목 없음</span>}</h3>
      </div>
      <Hero mediaId={mediaId} alt={title} className="max-h-64" />
      <Body text={body} compact={compact} />
    </Frame>
  )
}

function Counter({ n, limit }: { n: number; limit: number }) {
  const over = n > limit
  return (
    <span className={cn('text-meta tabular-nums', over ? 'font-semibold text-status-failed' : 'text-muted-foreground')}>
      {n} / {limit}
      {over && ' · 너무 길다'}
    </span>
  )
}

function CountedPreview({
  platform,
  title,
  body,
  account,
  mediaId,
  compact,
  className,
  limit,
  unit,
}: PostPreviewProps & { limit?: number; unit?: 'graphemes' | 'chars' | 'x' }) {
  const n = unit === 'graphemes' ? countGraphemes(body) : unit === 'x' ? xLength(body) : Array.from(body).length
  return (
    <Frame className={className}>
      <header className="flex items-center gap-2 text-meta">
        <PlatformMark platform={platform} />
        <span className="font-semibold text-foreground">{account ? `@${account}` : platformName(platform)}</span>
        {limit && <span className="ml-auto"><Counter n={n} limit={limit} /></span>}
      </header>
      <div className="rounded-2xl rounded-tl-sm bg-muted/60 px-3.5 py-3">
        <Body text={body} compact={compact} />
      </div>
      <Hero mediaId={mediaId} alt={title} className="max-h-72" />
      {platform === 'bluesky' && title && (
        <p className="text-meta text-muted-foreground">이미지 대체텍스트: {title}</p>
      )}
      {unit === 'x' && (
        <p className="text-meta text-muted-foreground">
          X 기준 글자 수 — 한글·이모지는 2자, 링크는 23자.{' '}
          {xHasLink(body) ? '링크가 들어 있어 한 건 $0.20이다.' : '링크가 없어 한 건 $0.015다.'}
        </p>
      )}
    </Frame>
  )
}

function ManualPreview({
  platform,
  title,
  body,
  channel,
  renderedBody,
  renderedReply,
  codeMode,
  compact,
  className,
}: PostPreviewProps) {
  return (
    <Frame className={className}>
      <header className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">
        <PlatformMark platform={platform} />
        <span className="font-semibold text-foreground">{platformName(platform)}</span>
        {channel && <span>{channel}</span>}
        <span className="ml-auto">직접 올리는 글</span>
      </header>
      {title && <h3 className="text-section font-semibold">{title}</h3>}
      <Body text={renderedBody || body} compact={compact} />
      {codeMode === 'reply' && (
        <div className="space-y-1 border-t border-border pt-3">
          <p className="text-meta font-medium text-muted-foreground">답글</p>
          <Body text={renderedReply ?? ''} compact={compact} />
        </div>
      )}
    </Frame>
  )
}
